import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Download, Printer, ArrowLeft, User, Building, CreditCard, AlertTriangle, Loader2 } from 'lucide-react';
import DateDropdownPicker from '../ui/DateDropdownPicker';
import { fetchIntermediaryData } from '../../utils/intermediaryData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEISGenerator = ({
  open,
  onClose,
  selectedOffer,
  loanAmount,
  loanPeriodMonths,
  interestType,
  productType,
  irccRate,
  propertyPrice,
  downPayment
}) => {
  const [step, setStep] = useState('form');
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [preview, setPreview] = useState(null);
  const [documentType] = useState('feis');
  const [propertyType, setPropertyType] = useState('apartament');
  const [remuneration, setRemuneration] = useState({ paidBy: 'creditor', clientPaysFee: false, percent: '', base: '', amount: '' });
  const [maxIrcc, setMaxIrcc] = useState('');
  const iframeRef = useRef(null);
  
  const [clientData, setClientData] = useState({
    fullName: '',
    cnp: '',
    idSeries: '',
    idNumber: '',
    idIssuedBy: '',
    idIssuedDate: '',
    idExpiryDate: '',
    birthDate: '',
    birthPlace: '',
    citizenship: 'Română',
    address: '',
    phone: '',
    email: '',
    employerName: '',
    employerAddress: '',
    monthlyIncome: '',
    incomeType: 'Salariat'
  });

  const [intermediaryData, setIntermediaryData] = useState({
    companyName: '',
    cui: '',
    jNumber: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    anpcNumber: '',
    representative: ''
  });

  useEffect(() => {
    if (!open) return;
    fetchIntermediaryData()
      .then((d) => {
        setIntermediaryData(d);
        if (d.remuneration) {
          setRemuneration({ paidBy: d.remuneration.paidBy || 'creditor', clientPaysFee: !!d.remuneration.clientPaysFee,
            percent: d.remuneration.percent ?? '', base: d.remuneration.base || '', amount: d.remuneration.amount ?? '' });
        }
      })
      .catch((err) => console.error('Nu s-au putut prelua datele firmei:', err));
    axios.get(`${API_URL}/api/ircc`)
      .then((res) => setMaxIrcc(res.data?.ircc_max_historical != null ? String(res.data.ircc_max_historical) : ''))
      .catch(() => {});
  }, [open]);

  const handleClientChange = (field, value) => {
    setClientData(prev => ({ ...prev, [field]: value }));
  };

  const handleIntermediaryChange = (field, value) => {
    setIntermediaryData(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (value) => {
    const num = Number(value);
    if (value === null || value === undefined || value === '' || Number.isNaN(num)) return '-';
    return new Intl.NumberFormat('ro-RO').format(Math.round(num)) + ' RON';
  };

  const formatPercent = (value) => {
    const num = Number(value);
    if (value === null || value === undefined || value === '' || Number.isNaN(num)) return '-';
    return num.toFixed(2) + '%';
  };

  // Calculează costul total REAL al creditului
  const validateForm = () => {
    const required = ['fullName', 'cnp', 'address', 'phone'];
    for (const field of required) {
      if (!clientData[field]?.trim()) {
        alert(`Vă rugăm completați câmpul: ${field === 'fullName' ? 'Nume complet' : field === 'cnp' ? 'CNP' : field === 'address' ? 'Adresa' : 'Telefon'}`);
        return false;
      }
    }
    return true;
  };

  const buildRequest = () => {
    const amount = parseFloat(loanAmount) || 0;
    const dp = parseFloat(downPayment) || 25;
    const price = parseFloat(propertyPrice) || (dp < 100 ? Math.round(amount / (1 - dp / 100)) : amount);
    return {
      clientData, intermediaryData, selectedOffer,
      loanAmount: amount,
      loanPeriodMonths: parseInt(loanPeriodMonths) || 0,
      interestType: interestType || 'fixed_5y',
      productType: productType || 'energy_class_bc',
      irccRate: parseFloat(irccRate) || null,
      propertyPrice: price,
      downPayment: dp,
      propertyType,
      documentType,
      indexInfo: {
        max_historical: maxIrcc === '' ? null : parseFloat(maxIrcc)
      },
      remuneration: {
        paidBy: remuneration.paidBy,
        clientPaysFee: remuneration.clientPaysFee,
        percent: remuneration.percent === '' ? null : parseFloat(remuneration.percent),
        base: remuneration.base || null,
        amount: remuneration.amount === '' ? null : parseFloat(remuneration.amount)
      }
    };
  };

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // Previzualizarea vine din backend: același HTML și aceleași calcule ca PDF-ul
  const handleGenerateFEIS = async () => {
    if (!validateForm()) return;
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const { data } = await axios.post(`${API_URL}/api/calculator/feis-html`, buildRequest(), { headers: authHeaders() });
      setPreview(data);
      setStep('preview');
    } catch (error) {
      console.error('Eroare previzualizare document:', error);
      setPreviewError(error?.response?.data?.detail ? JSON.stringify(error.response.data.detail) : 'Previzualizarea nu a putut fi generată. Datele introduse au fost păstrate.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePrint = () => {
    iframeRef.current?.contentWindow?.focus();
    iframeRef.current?.contentWindow?.print();
  };

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const response = await axios.post(`${API_URL}/api/calculator/feis-pdf`, buildRequest(), { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${documentType === 'feis' ? 'FEIS' : 'DocInformativ'}_${selectedOffer?.bank_name}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Eroare la descărcarea PDF:', error);
      let detail = '';
      try {
        const text = error?.response?.data instanceof Blob ? await error.response.data.text() : '';
        detail = text ? JSON.stringify(JSON.parse(text).detail) : '';
      } catch { /* ignore */ }
      alert(`Eroare la generarea PDF. Datele introduse au fost păstrate – încercați din nou.${detail ? `\n\nDetalii: ${detail}` : ''}`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Formular pentru editare date
  if (step === 'form') {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <CreditCard className="w-6 h-6 text-blue-600" />
              Generare FEIS - Fișa Europeană de Informații Standardizate
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Info despre oferta selectată */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Building className="w-4 h-4" />
                Oferta selectată: {selectedOffer?.bank_name}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-blue-600">Credit:</span>
                  <p className="font-medium">{formatCurrency(loanAmount)}</p>
                </div>
                <div>
                  <span className="text-blue-600">Perioadă:</span>
                  <p className="font-medium">{Math.round(loanPeriodMonths / 12)} ani</p>
                </div>
                <div>
                  <span className="text-blue-600">Dobândă fixă:</span>
                  <p className="font-medium">{formatPercent(selectedOffer?.fixed_rate)}</p>
                </div>
                <div>
                  <span className="text-blue-600">DAE calculator (orientativ):</span>
                  <p className="font-medium">{formatPercent(selectedOffer?.dae)}</p>
                </div>
              </div>
            </div>

            {/* Date Client */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-green-600" />
                Date Client (editabile)
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Nume complet *</Label>
                  <Input
                    value={clientData.fullName}
                    onChange={(e) => handleClientChange('fullName', e.target.value)}
                    placeholder="POPESCU ION-ALEXANDRU"
                    className="uppercase"
                  />
                </div>
                
                <div>
                  <Label>CNP *</Label>
                  <Input
                    value={clientData.cnp}
                    onChange={(e) => handleClientChange('cnp', e.target.value)}
                    placeholder="1850101123456"
                    maxLength={13}
                  />
                </div>
                
                <div>
                  <Label>Data nașterii</Label>
                  <DateDropdownPicker
                    id="feis-birthDate"
                    value={clientData.birthDate}
                    onChange={(val) => handleClientChange('birthDate', val)}
                  />
                </div>

                <div>
                  <Label>Serie CI</Label>
                  <Input
                    value={clientData.idSeries}
                    onChange={(e) => handleClientChange('idSeries', e.target.value.toUpperCase())}
                    placeholder="XY"
                    maxLength={2}
                    className="uppercase"
                  />
                </div>
                
                <div>
                  <Label>Număr CI</Label>
                  <Input
                    value={clientData.idNumber}
                    onChange={(e) => handleClientChange('idNumber', e.target.value)}
                    placeholder="123456"
                    maxLength={6}
                  />
                </div>

                <div>
                  <Label>Eliberat de</Label>
                  <Input
                    value={clientData.idIssuedBy}
                    onChange={(e) => handleClientChange('idIssuedBy', e.target.value)}
                    placeholder="SPCLEP Sector 1"
                  />
                </div>

                <div>
                  <Label>Data eliberării</Label>
                  <Input
                    type="date"
                    value={clientData.idIssuedDate}
                    onChange={(e) => handleClientChange('idIssuedDate', e.target.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Adresa domiciliu *</Label>
                  <Textarea
                    value={clientData.address}
                    onChange={(e) => handleClientChange('address', e.target.value)}
                    placeholder="Str. Exemplu nr. 10, Bl. A1, Sc. 2, Ap. 15, Sector 1, București"
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Telefon *</Label>
                  <Input
                    value={clientData.phone}
                    onChange={(e) => handleClientChange('phone', e.target.value)}
                    placeholder="0712 345 678"
                  />
                </div>

                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={clientData.email}
                    onChange={(e) => handleClientChange('email', e.target.value)}
                    placeholder="client@email.com"
                  />
                </div>
              </div>
            </div>

            {/* Tip document, tip imobil, remunerație */}
            <div className="border rounded-lg p-4 bg-slate-50 space-y-3" data-testid="feis-doc-options">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Document</Label>
                  <div className="w-full border rounded-md h-10 px-3 text-sm bg-white flex items-center font-medium text-slate-700" data-testid="feis-document-type">
                    FEIS – Fișa Europeană de Informații Standardizate (OUG 52/2016)
                  </div>
                </div>
                <div>
                  <Label>Tip imobil (pentru taxa de evaluare)</Label>
                  <select className="w-full border rounded-md h-10 px-3 text-sm bg-white" value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value)} data-testid="feis-property-type">
                    <option value="apartament">Apartament</option>
                    <option value="casa">Casă</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Comision de intermediere</Label>
                  <select className="w-full border rounded-md h-10 px-3 text-sm bg-white" value={remuneration.paidBy}
                    onChange={(e) => setRemuneration({ ...remuneration, paidBy: e.target.value, clientPaysFee: e.target.value === 'consumator' })}
                    data-testid="feis-remuneration-paidby">
                    <option value="creditor">Intermediarul este remunerat de creditor – consumatorul nu datorează comision</option>
                    <option value="consumator">Consumatorul datorează comision de intermediere</option>
                  </select>
                </div>
                {remuneration.paidBy === 'consumator' ? (
                  <div>
                    <Label>Suma comisionului (lei)</Label>
                    <Input type="number" step="0.01" value={remuneration.amount} placeholder="suma datorată de consumator"
                      onChange={(e) => setRemuneration({ ...remuneration, amount: e.target.value })} data-testid="feis-remuneration-amount" />
                  </div>
                ) : (
                  <div>
                    <Label>Procent comision de la bancă (%)</Label>
                    <Input type="number" step="0.01" value={remuneration.percent} placeholder="ex. 1.00"
                      onChange={(e) => setRemuneration({ ...remuneration, percent: e.target.value })} data-testid="feis-remuneration-percent" />
                    {remuneration.percent !== '' && !isNaN(parseFloat(remuneration.percent)) && (
                      <p className="text-xs text-emerald-700 mt-1" data-testid="feis-remuneration-computed">
                        Comision estimat: {formatCurrency((parseFloat(loanAmount) || 0) * parseFloat(remuneration.percent) / 100)}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <div>
                  <Label>IRCC maxim istoric – scenariu stres DAE (%)</Label>
                  <Input type="number" step="0.01" value={maxIrcc} placeholder="6.06"
                    onChange={(e) => setMaxIrcc(e.target.value)} data-testid="feis-max-ircc" />
                  <p className="text-xs text-gray-500 mt-1">Valoare preluată din Admin. O poți modifica doar pentru acest document.</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Suma comisionului apare în Secțiunea 2 a FEIS; scenariul de stres DAE folosește IRCC maxim istoric de mai sus.</p>
            </div>

            {/* Date Intermediar */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-purple-600" />
                Date Intermediar de Credite
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Denumire firmă</Label>
                  <Input
                    value={intermediaryData.companyName}
                    onChange={(e) => handleIntermediaryChange('companyName', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label>CUI</Label>
                  <Input
                    value={intermediaryData.cui}
                    onChange={(e) => handleIntermediaryChange('cui', e.target.value)}
                  />
                </div>

                <div>
                  <Label>Nr. Reg. Comerțului</Label>
                  <Input
                    value={intermediaryData.jNumber}
                    onChange={(e) => handleIntermediaryChange('jNumber', e.target.value)}
                    placeholder="J40/1234/2020"
                  />
                </div>

                <div>
                  <Label>Nr. Înregistrare ANPC</Label>
                  <Input
                    value={intermediaryData.anpcNumber}
                    onChange={(e) => handleIntermediaryChange('anpcNumber', e.target.value)}
                    placeholder="RAJ-12345"
                  />
                </div>

                <div>
                  <Label>Reprezentant</Label>
                  <Input
                    value={intermediaryData.representative}
                    onChange={(e) => handleIntermediaryChange('representative', e.target.value)}
                  />
                </div>

                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={intermediaryData.phone}
                    onChange={(e) => handleIntermediaryChange('phone', e.target.value)}
                  />
                </div>

                <div>
                  <Label>Email</Label>
                  <Input
                    value={intermediaryData.email}
                    onChange={(e) => handleIntermediaryChange('email', e.target.value)}
                  />
                </div>

                <div>
                  <Label>Website</Label>
                  <Input
                    value={intermediaryData.website}
                    onChange={(e) => handleIntermediaryChange('website', e.target.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Adresa sediu</Label>
                  <Input
                    value={intermediaryData.address}
                    onChange={(e) => handleIntermediaryChange('address', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Anulează
            </Button>
            <Button 
              onClick={handleGenerateFEIS}
              disabled={previewLoading}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="feis-generate-btn"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
              {previewLoading ? 'Se calculează...' : (documentType === 'feis' ? 'Generează FEIS' : 'Generează document informativ')}
            </Button>
          </div>
          {previewError && <p className="text-sm text-red-600 mt-2" data-testid="feis-preview-error">{previewError}</p>}
        </DialogContent>
      </Dialog>
    );
  }

  const missingItems = [...(preview?.missing || []), ...(preview?.checks || [])];
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[1100px] max-h-[95vh] overflow-hidden p-0 flex flex-col">
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10 gap-3">
          <Button variant="outline" onClick={() => setStep('form')} data-testid="feis-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Înapoi la editare
          </Button>
          <div className="text-center">
            <h2 className="text-base font-semibold" data-testid="feis-preview-title">
              FEIS – {selectedOffer?.bank_name}
            </h2>
            <span className="text-xs text-slate-500" data-testid="feis-preview-subtitle">Fișa Europeană de Informații Standardizate</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint} data-testid="feis-print-btn">
              <Printer className="w-4 h-4 mr-2" />
              Printează
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleDownloadPDF} disabled={downloadingPDF} data-testid="feis-download-pdf-btn">
              {downloadingPDF ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {downloadingPDF ? 'Se generează...' : 'Descarcă PDF'}
            </Button>
          </div>
        </div>
        {preview?.is_draft && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-800" data-testid="feis-missing-list">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            Câmpuri necompletate încă (apar automat după completarea lor în platformă): {missingItems.join(' · ')}
          </div>
        )}
        <iframe
          ref={iframeRef}
          title="Previzualizare document"
          srcDoc={preview?.html || ''}
          className="flex-1 w-full min-h-[70vh] bg-white"
          data-testid="feis-preview-frame"
        />
      </DialogContent>
    </Dialog>
  );
};

export default FEISGenerator;
